import React from "react";
import { QuestionContent, ChoiceContent } from "./QuestionContent";
import { RichMath } from "./KaTeX";
import { normalizeSolutionMath } from "../lib/solutionMath";
import { CheckCircle2, XCircle, Circle, Youtube } from "lucide-react";

// UX1.2 — post-completion blank reason labels (match backend BlankReason literals)
export const BLANK_REASON_LABELS = {
  TOPIC_UNKNOWN: "Konuyu bilmiyordum",
  NOT_UNDERSTOOD: "Soruyu anlamadım",
  TIME_LIMIT: "Zaman yetmedi / işim çıktı",
  NOT_SURE: "Emin olamadım",
  DISTRACTED: "Dikkatim dağıldı",
  OTHER: "Diğer",
};

/**
 * Shared expandable question-review row.
 *
 * Used by BOTH the session result screen (SessionResult) and the
 * "Son Çalışmalarım" review screen (SessionReview) so the same question
 * renders identically from either entry point. Data shape: one item from
 * `GET /api/sessions/{id}/review` (blocks, choices, selected, correct,
 * explanation, solution, video_url, result, blank_reason).
 */
export function QuestionReviewItem({ item: it, index, open, onToggle, tidPrefix = "review-row" }) {
  const isBlank = it.result === "BLANK";
  const isCorrect = it.is_correct || it.result === "CORRECT";
  return (
    <div data-testid={`${tidPrefix}-${index}`}
         className="bg-white rounded-xl border border-[#E2E8F0]">
      <button
        onClick={onToggle}
        data-testid={`${tidPrefix}-toggle-${index}`}
        className="w-full flex items-center gap-3 p-4 text-left">
        <span className="shrink-0 w-8 h-8 rounded-full grid place-items-center font-mono text-sm bg-slate-100 text-slate-700">
          {index + 1}
        </span>
        {isBlank ? <Circle size={18} className="text-slate-400" />
          : isCorrect ? <CheckCircle2 size={18} className="text-emerald-600" />
          : <XCircle size={18} className="text-rose-600" />}
        <span className="text-sm">
          {isBlank ? "Boş bırakıldı" : isCorrect ? "Doğru" : "Yanlış"}
        </span>
        {isBlank && it.blank_reason && BLANK_REASON_LABELS[it.blank_reason] && (
          <span className="text-[10px] text-slate-400 hidden sm:inline">
            · {BLANK_REASON_LABELS[it.blank_reason]}
          </span>
        )}
        <span className="ml-auto text-xs font-mono text-slate-400">{it.ref}</span>
      </button>
      {open && (
        <div className="border-t border-[#E2E8F0] p-4 sm:p-5 space-y-4">
          <div className="overflow-hidden">
            <QuestionContent question={{ ...it, blocks: it.blocks, text: it.text }} />
          </div>
          <div className="space-y-2">
            {(it.choices || []).map((c) => {
              const isSelected = it.selected === c.key;
              const isCorrectAnswer = it.correct === c.key;
              return (
                <div key={c.key} className={`rounded-xl border p-3 flex items-start gap-3 ${
                  isCorrectAnswer ? "border-emerald-500 bg-emerald-50"
                  : isSelected ? "border-rose-500 bg-rose-50"
                  : "border-[#E2E8F0]"}`}>
                  <span className={`shrink-0 w-8 h-8 rounded-full grid place-items-center font-mono text-sm ${
                    isCorrectAnswer ? "bg-emerald-500 text-white"
                    : isSelected ? "bg-rose-500 text-white"
                    : "bg-slate-100 text-slate-700"}`}>{c.key}</span>
                  <div className="min-w-0 flex-1"><ChoiceContent choice={c} /></div>
                </div>
              );
            })}
          </div>
          {isBlank && (
            <div className="text-xs text-slate-500">
              Bu soruya cevap vermedin
              {it.blank_reason && BLANK_REASON_LABELS[it.blank_reason]
                ? ` — ${BLANK_REASON_LABELS[it.blank_reason]}`
                : "."}
            </div>
          )}
          {it.explanation && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
              <div className="font-medium mb-1">Açıklama</div>
              <div className="whitespace-pre-wrap"><RichMath text={normalizeSolutionMath(it.explanation)} /></div>
            </div>
          )}
          {it.solution && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm" data-testid={`${tidPrefix}-solution-${index}`}>
              <div className="font-medium mb-1">Çözüm</div>
              <div className="whitespace-pre-wrap"><RichMath text={normalizeSolutionMath(it.solution)} /></div>
            </div>
          )}
          {it.video_url && (
            <a href={it.video_url} target="_blank" rel="noopener noreferrer"
              data-testid={`${tidPrefix}-video-${index}`}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-600 text-white text-sm hover:bg-rose-500">
              <Youtube size={16}/> Video Çözüm
            </a>
          )}
        </div>
      )}
    </div>
  );
}
